/**
 * The catalogue of map tools, and what each one can be configured with.
 *
 *   registry entry → Add Tool gallery → that tool's configuration → module.toolConfigs
 *
 * ONE source of truth. The gallery, the configuration form, the map's tool bar
 * and the viewer all read this, so adding a tool means adding an entry here
 * plus its panel — never touching four separate lists that then drift apart.
 *
 * `configFields` describes a tool's settings DECLARATIVELY rather than each
 * tool shipping a bespoke form. A search tool that needs "which layers" and
 * "which fields" and a filter tool that needs "which layer" are the same shape
 * of question, and the editor can render both without knowing what a search is.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Bookmark,
  Clock,
  Combine,
  Crosshair,
  Download,
  Filter,
  FileText,
  Flame,
  Layers,
  LocateFixed,
  MapPin,
  MousePointer,
  Move3d,
  PenLine,
  Printer,
  Ruler,
  Search,
  Share2,
  ShieldAlert,
  SlidersHorizontal,
  Table2,
  TextSearch,
  Circle,
  Upload,
  SquarePen,
  Navigation,
  Shapes,
  Scissors,
  Wand2,
  Target,
} from 'lucide-react';

/** Kinds of setting a tool can declare. */
export type ToolFieldKind =
  /** Pick one or more of the map's layers. */
  | 'layers'
  /** Pick fields from the layers chosen above. */
  | 'fields'
  /** Free text, e.g. a placeholder or a title. */
  | 'text'
  /** A number with bounds. */
  | 'number'
  /** On/off. */
  | 'boolean'
  /** One of a fixed list. */
  | 'select'
  /**
   * Any number of a fixed list, stored as an array of values.
   *
   * Distinct from `select` because "which basemaps do we offer" is not one
   * answer. The pairs that used to stand in for it ("All four", "Street and
   * satellite only") were arbitrary bundles an admin had to hope matched what
   * they wanted.
   */
  | 'multiselect';

export interface ToolField {
  key: string;
  label: string;
  kind: ToolFieldKind;
  hint?: string;
  /** `select` only. */
  options?: { value: string; label: string }[];
  /** `number` only. */
  min?: number;
  max?: number;
  defaultValue?: unknown;
  /** Shown only when another field has this value — keeps forms short. */
  showWhen?: { key: string; equals: unknown };
}

export interface ToolDefinition {
  id: string;
  name: string;
  /** One line in the gallery — what the tool is FOR, not what it looks like. */
  description: string;
  icon: LucideIcon;
  /** Groups the gallery so twenty tools stay scannable. */
  category: 'Navigate' | 'Query' | 'Measure' | 'Edit' | 'Share';
  /** Empty means the tool has nothing to configure. */
  configFields: ToolField[];
}

/** Per-tool settings, keyed by tool id. Stored on the module. */
export type ToolConfigs = Record<string, Record<string, unknown>>;

export const TOOL_REGISTRY: ToolDefinition[] = [
  /* ── Query ───────────────────────────────────────────────────────────── */
  {
    id: 'search',
    name: 'Search',
    /* Sibling of Query, not a duplicate of it. Query asks someone to BUILD a
       condition — layer, field, operator, value — which is right when you know
       how the data is stored. This one takes the value alone and looks for it
       everywhere the admin allowed, which is what someone holding a meter
       number actually has. */
    description: 'Find features by value, across every layer and field you allow.',
    icon: TextSearch,
    category: 'Query',
    configFields: [
      {
        key: 'layers',
        label: 'Searchable layers',
        kind: 'layers',
        hint: 'Leave empty to search every layer in the map.',
      },
      {
        key: 'fields',
        label: 'Searchable fields',
        kind: 'fields',
        /* Not the usual "leave empty for everything" note alone: here the
           setting is also what keeps the search quick, because every field
           listed becomes another clause the service has to scan. */
        hint: 'Leave empty for every field. Naming just the ones people search by keeps it fast.',
      },
      {
        key: 'placeholder',
        label: 'Placeholder text',
        kind: 'text',
        defaultValue: 'Search features…',
      },
      {
        key: 'maxSuggestions',
        label: 'Suggestions shown',
        kind: 'number',
        min: 3,
        max: 12,
        defaultValue: 6,
        hint: 'Shared across the searched layers, so every layer is represented.',
      },
      { key: 'zoomOnSelect', label: 'Zoom to the chosen feature', kind: 'boolean', defaultValue: true },
    ],
  },
  {
    id: 'query',
    // Named for what it is. The tab said "Search" while the panel it opened
    // said "Query Features", so the same tool had two names on screen.
    name: 'Query',
    description: 'Find features by building a condition on their attributes.',
    icon: Search,
    category: 'Query',
    configFields: [
      {
        key: 'layers',
        label: 'Queryable layers',
        kind: 'layers',
        hint: 'Leave empty to allow every layer in the map.',
      },
      {
        key: 'fields',
        label: 'Queryable fields',
        kind: 'fields',
        hint: 'Leave empty to offer every field on those layers.',
      },
      {
        key: 'placeholder',
        label: 'Placeholder text',
        kind: 'text',
        defaultValue: 'Search features…',
      },
      { key: 'exactMatch', label: 'Exact match only', kind: 'boolean', defaultValue: false },
      { key: 'maxResults', label: 'Maximum results', kind: 'number', min: 1, max: 200, defaultValue: 25 },
      { key: 'zoomOnSelect', label: 'Zoom to the chosen result', kind: 'boolean', defaultValue: true },
    ],
  },
  {
    id: 'filter',
    name: 'Filter',
    description: 'Narrow a layer to the features that match a condition.',
    icon: Filter,
    category: 'Query',
    configFields: [
      { key: 'layers', label: 'Filterable layers', kind: 'layers' },
      { key: 'fields', label: 'Filterable fields', kind: 'fields' },
      { key: 'allowMultiple', label: 'Allow several conditions', kind: 'boolean', defaultValue: true },
    ],
  },
  {
    id: 'select',
    name: 'Select',
    description: 'Drag a shape over the map to pick features out of a layer.',
    icon: MousePointer,
    category: 'Query',
    /* Shape is NOT configured. Rectangle, polygon and circle are three
       ordinary ways to drag a box; pinning one at configuration time meant an
       admin choosing once, for everyone, which gesture a person was allowed —
       a setting that only ever takes options away. All three are offered in
       the panel. Which LAYERS may be selected is a real decision, so that
       stays. */
    configFields: [
      { key: 'layers', label: 'Selectable layers', kind: 'layers' },
      { key: 'showTable', label: 'Offer "Open in table"', kind: 'boolean', defaultValue: true },
    ],
  },
  {
    id: 'selectLocation',
    name: 'Select by location',
    /* The third way to select, and the one the other two cannot express.
       Query selects by attribute, Select selects by a shape you draw; this
       selects by a shape already on the map, which is the only one of the
       three that can answer "everything inside Ward 7". */
    description: "Select features in one layer using another layer's geometry.",
    icon: Shapes,
    category: 'Query',
    configFields: [
      { key: 'layers', label: 'Selectable layers', kind: 'layers' },
      {
        key: 'relationship',
        label: 'Default relationship',
        kind: 'select',
        options: [
          { value: 'intersect', label: 'Intersect' },
          { value: 'withinDistance', label: 'Are within a distance of' },
          { value: 'within', label: 'Are completely within' },
          { value: 'contains', label: 'Completely contain' },
        ],
        defaultValue: 'intersect',
      },
      {
        key: 'distance',
        label: 'Default search distance',
        kind: 'number',
        min: 1,
        max: 100000,
        defaultValue: 100,
        /* Only asked about when the default relationship is the one that uses
           it — the field is inert for the other three. */
        showWhen: { key: 'relationship', equals: 'withinDistance' },
      },
      {
        key: 'unit',
        label: 'Distance unit',
        kind: 'select',
        options: [
          { value: 'm', label: 'Metres' },
          { value: 'km', label: 'Kilometres' },
          { value: 'ft', label: 'Feet' },
          { value: 'mi', label: 'Miles' },
        ],
        defaultValue: 'm',
        showWhen: { key: 'relationship', equals: 'withinDistance' },
      },
    ],
  },
  {
    id: 'identify',
    name: 'Identify',
    description: 'Click the map to read every attribute at that point.',
    icon: Crosshair,
    category: 'Query',
    configFields: [
      { key: 'layers', label: 'Layers to identify', kind: 'layers' },
      { key: 'tolerance', label: 'Click tolerance (px)', kind: 'number', min: 1, max: 30, defaultValue: 6 },
    ],
  },
  {
    id: 'attributeTable',
    name: 'Attribute table',
    description: 'Open a layer’s records in a sortable, filterable table.',
    icon: Table2,
    category: 'Query',
    configFields: [
      { key: 'layers', label: 'Layers offered', kind: 'layers' },
    ],
  },

  {
    id: 'trace',
    name: 'Network trace',
    description: 'Follow a pipe through everything it is joined to, or find which valves isolate an area.',
    icon: Share2,
    category: 'Query',
    configFields: [
      {
        key: 'layers',
        label: 'Network layers',
        kind: 'layers',
        hint: 'Line layers carry the trace; point layers on them are reported as assets (or, in Valve Isolation mode, chosen as the valve layer).',
      },
      {
        key: 'tolerance',
        label: 'Join tolerance (m)',
        kind: 'number',
        min: 0.01,
        max: 50,
        defaultValue: 1,
        hint: 'How close two pipe ends must be to count as joined.',
      },
      { key: 'includeAssets', label: 'Report assets on the run', kind: 'boolean', defaultValue: true },
      {
        key: 'maxFeatures',
        label: 'Stop after this many segments',
        kind: 'number',
        min: 50,
        max: 20000,
        defaultValue: 2000,
        hint: 'A guard against a badly snapped drawing tracing the whole city.',
      },
    ],
  },
  {
    id: 'buffer',
    name: 'Buffer',
    description: 'Draw a ring at a set distance and list what falls inside it.',
    icon: Circle,
    category: 'Query',
    configFields: [
      { key: 'layers', label: 'Layers to test against', kind: 'layers' },
      { key: 'distance', label: 'Default distance (m)', kind: 'number', min: 1, max: 100000, defaultValue: 250 },
      {
        key: 'unit',
        label: 'Distance unit',
        kind: 'select',
        options: [
          { value: 'm', label: 'Metres' },
          { value: 'km', label: 'Kilometres' },
          { value: 'ft', label: 'Feet' },
          { value: 'mi', label: 'Miles' },
        ],
        defaultValue: 'm',
      },
      { key: 'listResults', label: 'List the features found', kind: 'boolean', defaultValue: true },
    ],
  },
  {
    id: 'timeSlider',
    name: 'Time Slider',
    description: 'Play back or scrub a layer over a date field, filtering the map as you go.',
    icon: Clock,
    category: 'Query',
    configFields: [
      {
        key: 'layers',
        label: 'Layers to filter',
        kind: 'layers',
        hint: 'Leave empty to allow every layer in the map.',
      },
    ],
  },
  {
    id: 'density',
    name: 'Density',
    /* Esri's Point Density / Line Density, browser-side: a kernel density
       surface rendered as a heatmap rather than a raster, since this app has
       no raster analysis engine. A line has no direct equivalent, so it is
       approximated by sampling points along its length — see DensityPanel's
       own doc comment for that trade-off. */
    description: 'Show where a point or line layer clusters most, as a heatmap.',
    icon: Flame,
    category: 'Query',
    configFields: [
      {
        key: 'layers',
        label: 'Layers offered',
        kind: 'layers',
        hint: 'Point or line layers only — a polygon layer has nothing for this to sample.',
      },
      {
        key: 'defaultRadius',
        label: 'Default point radius (px)',
        kind: 'number',
        min: 5,
        max: 100,
        defaultValue: 25,
        hint: 'How far one point’s influence spreads on screen. Larger blends nearby points into broader hot spots.',
      },
      {
        key: 'defaultGradient',
        label: 'Default colour ramp',
        kind: 'select',
        options: [
          { value: 'heat', label: 'Heat (blue → red)' },
          { value: 'fire', label: 'Fire (yellow → red)' },
          { value: 'cool', label: 'Cool (cyan → blue)' },
          { value: 'grass', label: 'Grass (yellow → green)' },
          { value: 'purple', label: 'Purple' },
          { value: 'grayscale', label: 'Grayscale' },
        ],
        defaultValue: 'heat',
      },
    ],
  },
  {
    id: 'joinFeatures',
    name: 'Join features',
    description: "Attach one layer's attributes to another, by location or matching values, as a new layer.",
    icon: Combine,
    category: 'Query',
    configFields: [
      { key: 'layers', label: 'Layers this tool may enrich', kind: 'layers' },
      {
        key: 'defaultRelationship',
        label: 'Default spatial relationship',
        kind: 'select',
        options: [
          { value: 'intersect', label: 'Intersect' },
          { value: 'withinDistance', label: 'Are within a distance of' },
          { value: 'within', label: 'Are completely within' },
          { value: 'contains', label: 'Completely contain' },
        ],
        defaultValue: 'intersect',
      },
      {
        key: 'defaultDistance',
        label: 'Default search distance',
        kind: 'number',
        min: 1,
        max: 100000,
        defaultValue: 100,
        showWhen: { key: 'defaultRelationship', equals: 'withinDistance' },
      },
      {
        key: 'unit',
        label: 'Distance unit',
        kind: 'select',
        options: [
          { value: 'm', label: 'Metres' },
          { value: 'km', label: 'Kilometres' },
          { value: 'ft', label: 'Feet' },
          { value: 'mi', label: 'Miles' },
        ],
        defaultValue: 'm',
        showWhen: { key: 'defaultRelationship', equals: 'withinDistance' },
      },
    ],
  },
  {
    id: 'findProblems',
    name: 'Find Problems',
    description: 'Scan layers for bad geometry, duplicates, missing data, disconnected pipes and more.',
    icon: ShieldAlert,
    category: 'Query',
    configFields: [
      { key: 'layers', label: 'Layers this tool may scan', kind: 'layers' },
      {
        key: 'defaultSnapToleranceMeters',
        label: 'Default connectivity snap tolerance (metres)',
        kind: 'number',
        min: 0.01,
        max: 1000,
        defaultValue: 0.5,
        hint: 'How close two line ends must be to count as connected.',
      },
      {
        key: 'defaultOverlapPercent',
        label: 'Default minimum overlap to flag (%)',
        kind: 'number',
        min: 0,
        max: 100,
        defaultValue: 1,
      },
    ],
  },

  /* ── Navigate ────────────────────────────────────────────────────────── */
  {
    id: 'bookmark',
    name: 'Bookmark',
    description: 'Save map views and return to them in one click.',
    icon: Bookmark,
    category: 'Navigate',
    configFields: [
      { key: 'allowUserBookmarks', label: 'Let end users add their own', kind: 'boolean', defaultValue: true },
    ],
  },
  {
    id: 'assetSearch',
    name: 'Near me',
    description: 'Find features within a distance of a point on the map.',
    icon: LocateFixed,
    category: 'Navigate',
    configFields: [
      { key: 'layers', label: 'Layers to search', kind: 'layers' },
      { key: 'radius', label: 'Default radius (m)', kind: 'number', min: 10, max: 50000, defaultValue: 500 },
      { key: 'useMyLocation', label: 'Offer “use my location”', kind: 'boolean', defaultValue: true },
    ],
  },
  {
    id: 'coordinates',
    name: 'Coordinates',
    description: 'Read the cursor’s position, and jump to a typed coordinate.',
    icon: MapPin,
    category: 'Navigate',
    configFields: [
      {
        key: 'format',
        label: 'Format',
        kind: 'select',
        options: [
          { value: 'dd', label: 'Decimal degrees' },
          { value: 'dms', label: 'Degrees / minutes / seconds' },
        ],
        defaultValue: 'dd',
      },
      { key: 'decimals', label: 'Decimal places', kind: 'number', min: 0, max: 8, defaultValue: 5 },
    ],
  },
  {
    id: 'basemapGallery',
    name: 'Basemap',
    description: 'Switch the background map.',
    icon: Layers,
    category: 'Navigate',
    configFields: [
      {
        key: 'basemaps',
        label: 'Offered basemaps',
        kind: 'multiselect',
        hint: 'Leave empty to offer all of them.',
        options: [
          { value: 'street', label: 'Streets' },
          { value: 'terrain', label: 'Topographic' },
          { value: 'satellite', label: 'Imagery' },
        ],
        defaultValue: [],
      },
    ],
  },
  {
    id: 'direction',
    name: 'Directions',
    description: 'A driving route between two places, with turn-by-turn directions.',
    icon: Navigation,
    category: 'Navigate',
    configFields: [
      {
        key: 'unit',
        label: 'Distance unit',
        kind: 'select',
        options: [
          { value: 'km', label: 'Kilometres' },
          { value: 'mi', label: 'Miles' },
          { value: 'm', label: 'Metres' },
        ],
        defaultValue: 'km',
      },
      /* Travel mode is deliberately NOT offered. The public routing service
         this uses only has the driving profile loaded — asking it for walking
         or cycling returns the driving route with driving times, which a
         selector would present as three different answers. See
         services/routingService.ts. */
      { key: 'showSteps', label: 'Show turn-by-turn directions', kind: 'boolean', defaultValue: true },
    ],
  },

  /* ── Measure ─────────────────────────────────────────────────────────── */
  {
    id: 'measure',
    name: 'Measure',
    description: 'Measure distance and area on the map.',
    icon: Ruler,
    category: 'Measure',
    configFields: [
      {
        key: 'lengthUnit',
        label: 'Length unit',
        kind: 'select',
        options: [
          { value: 'metric', label: 'Metres / kilometres' },
          { value: 'imperial', label: 'Feet / miles' },
        ],
        defaultValue: 'metric',
      },
      {
        key: 'areaUnit',
        label: 'Area unit',
        kind: 'select',
        options: [
          { value: 'sqm', label: 'Square metres / km²' },
          { value: 'hectare', label: 'Hectares' },
          { value: 'acre', label: 'Acres' },
        ],
        defaultValue: 'sqm',
      },
    ],
  },
  {
    id: 'elevation',
    name: 'Coordinates readout',
    description: 'Live latitude and longitude under the cursor.',
    icon: Move3d,
    category: 'Measure',
    configFields: [
      { key: 'decimals', label: 'Decimal places', kind: 'number', min: 0, max: 8, defaultValue: 5 },
      { key: 'showScale', label: 'Show map scale', kind: 'boolean', defaultValue: true },
    ],
  },

  /* ── Edit ────────────────────────────────────────────────────────────── */
  {
    id: 'draw',
    name: 'Draw',
    description: 'Sketch points, lines and shapes over the map.',
    icon: PenLine,
    category: 'Edit',
    configFields: [
      {
        key: 'shapes',
        label: 'Shapes offered',
        kind: 'multiselect',
        hint: 'Leave empty to offer all four.',
        options: [
          { value: 'point', label: 'Point' },
          { value: 'line', label: 'Line' },
          { value: 'polygon', label: 'Polygon' },
          { value: 'circle', label: 'Circle' },
        ],
        defaultValue: [],
      },
      { key: 'allowLabels', label: 'Allow text labels', kind: 'boolean', defaultValue: true },
    ],
  },
  {
    id: 'addData',
    name: 'Add data',
    description: 'Let end users drop a file onto the map — GeoJSON, KML, shapefile or CSV.',
    icon: Upload,
    category: 'Edit',
    configFields: [
      {
        key: 'formats',
        label: 'Accepted formats',
        kind: 'multiselect',
        hint: 'Leave empty to accept every format the parser supports.',
        options: [
          { value: 'geojson', label: 'GeoJSON' },
          { value: 'kml', label: 'KML / KMZ' },
          { value: 'shapefile', label: 'Shapefile (.zip)' },
          { value: 'csv', label: 'CSV / TSV' },
        ],
        defaultValue: [],
      },
      // 12 MB is the parser's own ceiling (MAX_LOCAL_FILE_BYTES); offering a
      // larger number here would promise something the import would refuse.
      { key: 'maxSizeMb', label: 'Maximum file size (MB)', kind: 'number', min: 1, max: 12, defaultValue: 12 },
    ],
  },
  {
    id: 'edit',
    name: 'Edit',
    description: 'Change feature attributes — written back to the service, or to the file layer itself.',
    icon: SquarePen,
    category: 'Edit',
    configFields: [
      { key: 'layers', label: 'Editable layers', kind: 'layers' },
      { key: 'fields', label: 'Editable fields', kind: 'fields' },
      { key: 'confirmBeforeSave', label: 'Confirm before saving', kind: 'boolean', defaultValue: true },
    ],
  },
  {
    id: 'swipe',
    name: 'Swipe',
    description: 'Drag a divider to compare two layers.',
    icon: SlidersHorizontal,
    category: 'Edit',
    configFields: [
      { key: 'layers', label: 'Layers to compare', kind: 'layers', hint: 'Choose exactly two.' },
      {
        key: 'direction',
        label: 'Divider direction',
        kind: 'select',
        options: [
          { value: 'vertical', label: 'Vertical' },
          { value: 'horizontal', label: 'Horizontal' },
        ],
        defaultValue: 'vertical',
      },
    ],
  },

  /* ── Share ───────────────────────────────────────────────────────────── */
  {
    id: 'print',
    name: 'Print',
    description: 'Export the current map view as an image or PDF.',
    icon: Printer,
    category: 'Share',
    configFields: [
      {
        key: 'layout',
        label: 'Default layout',
        kind: 'select',
        options: [
          { value: 'a4-landscape', label: 'A4 landscape' },
          { value: 'a4-portrait', label: 'A4 portrait' },
          { value: 'screen', label: 'Current screen size' },
        ],
        defaultValue: 'a4-landscape',
      },
      { key: 'includeLegend', label: 'Include the legend', kind: 'boolean', defaultValue: true },
      { key: 'title', label: 'Default title', kind: 'text' },
    ],
  },
  {
    id: 'share',
    name: 'Share',
    description: 'Copy a link that reopens the map at the current view.',
    icon: Share2,
    category: 'Share',
    configFields: [
      { key: 'includeView', label: 'Include the current extent in the link', kind: 'boolean', defaultValue: true },
    ],
  },
  {
    id: 'exportKml',
    name: 'Export KML',
    description: 'Save a layer’s features as a KML file, to open in Google Earth or another GIS.',
    icon: Download,
    category: 'Share',
    configFields: [
      {
        key: 'layers',
        label: 'Exportable layers',
        kind: 'layers',
        hint: 'Leave empty to allow every layer in the map.',
      },
      {
        key: 'maxFeatures',
        label: 'Maximum features per export',
        kind: 'number',
        min: 100,
        max: 20000,
        defaultValue: 5000,
        hint: 'A guard against exporting a whole city’s worth of a layer by accident.',
      },
    ],
  },
  {
    id: 'report',
    name: 'Report',
    description: 'A feature-by-feature report — a map thumbnail next to the attributes you choose.',
    icon: FileText,
    category: 'Share',
    configFields: [
      {
        key: 'layers',
        label: 'Layers it can report on',
        kind: 'layers',
        hint: 'Leave empty to allow every layer in the map.',
      },
    ],
  },
  {
    id: 'clip',
    name: 'Clip',
    description: "Cut a polygon or line layer down to a boundary layer's polygons.",
    icon: Scissors,
    category: 'Query',
    configFields: [
      {
        key: 'layers',
        label: 'Layers that can be clipped',
        kind: 'layers',
        hint: 'Leave empty to allow every line or polygon layer in the map.',
      },
    ],
  },
  {
    id: 'digitize',
    name: 'Digitize',
    description: 'Trace a scanned drawing automatically into line and polygon features.',
    icon: Wand2,
    category: 'Query',
    configFields: [
      {
        key: 'layers',
        label: 'Rasters that can be digitized',
        kind: 'layers',
        hint: 'Leave empty to allow every image/GeoTIFF layer in the map.',
      },
    ],
  },
  {
    id: 'georeference',
    name: 'Georeference',
    description: 'Align a scanned drawing or photo to the map using control points.',
    icon: Target,
    category: 'Edit',
    configFields: [
      {
        key: 'layers',
        label: 'Rasters that can be refined',
        kind: 'layers',
        hint: 'Leave empty to allow every image/GeoTIFF layer in the map.',
      },
    ],
  },
];

export const TOOL_CATEGORIES: ToolDefinition['category'][] = [
  'Query',
  'Navigate',
  'Measure',
  'Edit',
  'Share',
];

export function getTool(id: string): ToolDefinition | undefined {
  return TOOL_REGISTRY.find((tool) => tool.id === id);
}

/** A tool's settings, with every declared default filled in. */
export function toolConfigWithDefaults(
  id: string,
  stored: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const tool = getTool(id);
  if (!tool) return stored ?? {};

  const resolved: Record<string, unknown> = {};
  for (const field of tool.configFields) {
    if (field.defaultValue !== undefined) resolved[field.key] = field.defaultValue;
  }
  return { ...resolved, ...(stored ?? {}) };
}

/** Fields whose `showWhen` condition is satisfied by the current values. */
export function visibleFields(
  tool: ToolDefinition,
  values: Record<string, unknown>,
): ToolField[] {
  return tool.configFields.filter(
    (field) => !field.showWhen || values[field.showWhen.key] === field.showWhen.equals,
  );
}
